import React, { useEffect, useState } from "react";
import { ThreeDots } from "react-loader-spinner";
import { Modal, Select, Button, DateRange } from "../../../../ui/index.ts";

import { useDownloadTimesheetReport } from "../../hooks/use-download-timehseet-reports.ts";
import {
  defaultRangeForPeriod,
  formatWeekLabel,
  MONTH_OPTIONS,
  PERIOD_OPTIONS,
  periodRangeToApiDates,
  toIsoDate,
  type DownloadDateRange,
  type DownloadPeriod,
} from "../../../../../utils/period-range.ts";
import styles from "./download-report-modal.module.css";

interface DownloadReportModalProps {
  open: boolean;
  onClose: () => void;
}

export default function DownloadReportModal({
  open,
  onClose,
}: DownloadReportModalProps) {
  const [period, setPeriod] = useState<DownloadPeriod>("MONTHLY");
  const [range, setRange] = useState<DownloadDateRange>(
    defaultRangeForPeriod("MONTHLY"),
  );

  const { download, isDownloading } = useDownloadTimesheetReport();

  useEffect(() => {
    if (open) {
      setPeriod("MONTHLY");
      setRange(defaultRangeForPeriod("MONTHLY"));
    }
  }, [open]);

  function handlePeriodChange(value: string) {
    const p = value as DownloadPeriod;
    setPeriod(p);
    setRange(defaultRangeForPeriod(p));
  }

  async function handleDownload() {
    const dates = periodRangeToApiDates(range);
    if (!dates) return;
    await download(dates.from, dates.to);
    onClose();
  }

  const canSubmit = periodRangeToApiDates(range) !== null;
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const filteredMonthOptions = MONTH_OPTIONS.filter(
    (opt) => Number(opt.value) <= currentMonth,
  );

  const footer = (
    <div className={styles.footer}>
      <Button variant="muted-outline" onClick={onClose}>
        Cancel
      </Button>
      <Button
        onClick={() => void handleDownload()}
        disabled={!canSubmit || isDownloading}
      >
        {isDownloading ? (
          <ThreeDots height="10" width="30" color="#fff" />
        ) : (
          "Download"
        )}
      </Button>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Download Timesheet Report"
      size="md"
      footer={footer}
    >
      <div className={styles.body}>
        <div className={styles.row}>
          <div className={styles.selectWrapper}>
            <Select
              label="Duration"
              value={period}
              onChange={handlePeriodChange}
              options={PERIOD_OPTIONS}
            />
          </div>

          {period === "MONTHLY" && range.type === "MONTHLY" && (
            <div className={styles.selectWrapper}>
              <Select
                label="Month"
                value={String(range.month)}
                onChange={(v) =>
                  setRange({
                    type: "MONTHLY",
                    year: currentYear,
                    month: Number(v),
                  })
                }
                options={filteredMonthOptions}
              />
            </div>
          )}

          {period === "WEEKLY" && range.type === "WEEKLY" && (
            <div className={styles.weekReadOnly}>
              <span className={styles.weekLabel}>Current week</span>
              <span className={styles.weekRange}>
                {formatWeekLabel(range.weekStart, range.weekEnd)}
              </span>
            </div>
          )}

          {period === "CUSTOM" && range.type === "CUSTOM" && (
            <div className={styles.selectWrapper}>
              <DateRange
                label="Date range"
                placeholderText="Select Date Range"
                maxDate={now}
                startDate={
                  range.startDate
                    ? new Date(`${range.startDate}T12:00:00`)
                    : null
                }
                endDate={
                  range.endDate ? new Date(`${range.endDate}T12:00:00`) : null
                }
                onChange={([s, e]: [Date | null, Date | null]) =>
                  setRange({
                    type: "CUSTOM",
                    startDate: s ? toIsoDate(s) : null,
                    endDate: e ? toIsoDate(e) : null,
                  })
                }
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
